import * as channel from "./channel.ts";
import type {Appearance} from "./topic_appearance.ts";

export const topic_permission_modes = {
    whitelist: 1,
    blacklist: 2,
} as const;

export type TopicPermissionAccess = {
    direct_members: number[];
    direct_subgroups: number[];
};

type TopicCapabilities = {
    can_manage: boolean;
    can_create: boolean;
    can_post: boolean;
    rendered_description: string | null;
};

type TopicPermissionsResponse = TopicCapabilities &
    (
        | {can_manage: false; enabled: false}
        | {
              can_manage: true;
              enabled: boolean;
              permission_mode: number;
              read_access: TopicPermissionAccess;
              post_access: TopicPermissionAccess;
              manage_access: TopicPermissionAccess;
              history_public_to_subscribers: boolean | null;
              creator_user_id: number | null;
              icon: Appearance["icon"];
              icon_color: string | null;
              icon_url?: string | null;
              icon_format?: "svg" | "webp";
          } & Appearance & {description: string | null}
    );

type CapabilityEntry = {
    can_post: boolean | undefined;
    retry_after?: number;
};
const capabilities = new Map<string, CapabilityEntry>();
const capability_listeners = new Set<() => void>();

export function normalize_topic(topic: string): string {
    const trimmed = topic.trim();
    return trimmed === "(no topic)" || trimmed === "general chat" ? "" : trimmed;
}

export function capability_key(stream_id: number, topic: string): string {
    return JSON.stringify([stream_id, normalize_topic(topic).toLowerCase()]);
}

export function on_capabilities_changed(listener: () => void): () => void {
    capability_listeners.add(listener);
    return () => capability_listeners.delete(listener);
}

function notify_capabilities_changed(): void {
    for (const listener of capability_listeners) {
        listener();
    }
}

export function invalidate_capabilities(): void {
    capabilities.clear();
    reset_compose_target();
    notify_capabilities_changed();
}

export function invalidate_streams(stream_ids: Set<number>): void {
    for (const key of capabilities.keys()) {
        const [stream_id] = JSON.parse(key) as [number, string];
        if (stream_ids.has(stream_id)) {
            capabilities.delete(key);
        }
    }
    // Keep the compose target and its timer; only its permission is stale.
    notify_capabilities_changed();
}

// Undefined means the request is pending. Neither pending nor failed requests
// may enable posting. Responses for an invalidated request are discarded.
export function can_post(stream_id: number, topic: string): boolean | undefined {
    const key = capability_key(stream_id, topic);
    const cached = capabilities.get(key);
    if (
        cached !== undefined &&
        (cached.retry_after === undefined || Date.now() < cached.retry_after)
    ) {
        return cached.can_post;
    }
    const entry: CapabilityEntry = {can_post: undefined};
    capabilities.set(key, entry);
    function complete(allowed: boolean | undefined, failed = false): void {
        if (capabilities.get(key) !== entry) {
            return;
        }
        entry.can_post = allowed;
        if (failed) {
            // A later recipient check can retry a failed request without
            // treating failure as permission or retrying on every keystroke.
            entry.retry_after = Date.now() + 5000;
        }
        notify_capabilities_changed();
    }
    fetch_topic_permissions(
        stream_id,
        normalize_topic(topic),
        (response) => complete(
            typeof response.can_post === "boolean" ? response.can_post : undefined,
            typeof response.can_post !== "boolean",
        ),
        () => complete(undefined, true),
    );
    return entry.can_post;
}

let compose_target: {key: string; stable: boolean; timer: ReturnType<typeof setTimeout>} | undefined;
export function reset_compose_target(): void {
    if (compose_target) {
        clearTimeout(compose_target.timer);
        // Discard even an in-flight response for a previous compose target.
        capabilities.delete(compose_target.key);
        compose_target = undefined;
    }
}

export function discard_stale_compose_target(stream_id: number | undefined, topic: string): void {
    if (stream_id === undefined || compose_target?.key !== capability_key(stream_id, topic)) {
        reset_compose_target();
    }
}

// This delays only the denial UI. Pending checks still disable sending, and
// explicit sends use can_post, never this presentation delay.
export function can_post_for_compose(stream_id: number, topic: string): boolean | undefined {
    const key = capability_key(stream_id, topic);
    if (compose_target?.key !== key) {
        reset_compose_target();
        const target = {
            key, stable: false,
            timer: setTimeout(() => {
                if (compose_target !== target) {
                    return;
                }
                target.stable = true;
                notify_capabilities_changed();
            }, 400),
        };
        compose_target = target;
    }
    const allowed = can_post(stream_id, topic);
    return allowed === false && !compose_target.stable ? undefined : allowed;
}

export function fetch_topic_permissions(
    stream_id: number,
    topic: string,
    success: (topic_permissions: TopicPermissionsResponse) => void,
    error?: JQuery.Ajax.ErrorCallback<unknown>,
    for_creation = false,
): void {
    void channel.get({
        url: "/json/topic_permissions",
        data: {
            stream_id,
            topic,
            ...(for_creation ? {for_creation: true} : {}),
        },
        success(data) {
            success(data.topic_permissions as TopicPermissionsResponse);
        },
        error,
    });
}

export function update_topic_permissions(
    stream_id: number,
    topic: string,
    topic_permissions: Partial<Appearance> & {
        permission_mode?: number;
        read_access?: TopicPermissionAccess;
        post_access?: TopicPermissionAccess;
        manage_access?: TopicPermissionAccess;
        history_public_to_subscribers?: boolean | null;
        icon?: Appearance["icon"];
        icon_color?: string | null;
        icon_file?: File;
        description?: string | null;
        create_only?: boolean;
    },
    success?: () => void,
    error?: JQuery.Ajax.ErrorCallback<unknown>,
): void {
    const data = {
        stream_id,
        topic,
        ...(topic_permissions.permission_mode !== undefined
            ? {permission_mode: topic_permissions.permission_mode} : {}),
        ...(topic_permissions.icon !== undefined
            ? {icon: JSON.stringify(topic_permissions.icon)}
            : {}),
        ...(topic_permissions.icon_color !== undefined
            ? {icon_color: JSON.stringify(topic_permissions.icon_color)}
            : {}),
        ...Object.fromEntries(
            ["read_access", "post_access", "manage_access", "history_public_to_subscribers",
                "icon_color_light", "icon_color_dark", "text_color_light", "text_color_dark",
                "emoji", "description", "create_only"].flatMap((field) => {
                const value = topic_permissions[field as keyof typeof topic_permissions];
                return value === undefined ? [] : [[field, JSON.stringify(value)]];
            }),
        ),
    };
    let request_data: typeof data | FormData = data;
    if (topic_permissions.icon_file) {
        const form = new FormData();
        for (const [key, value] of Object.entries(data)) {
            form.append(key, String(value));
        }
        form.append("icon_file", topic_permissions.icon_file);
        request_data = form;
    }
    void channel.post({
        url: "/json/topic_permissions",
        data: request_data,
        processData: topic_permissions.icon_file ? false : undefined,
        contentType: topic_permissions.icon_file ? false : undefined,
        success() {
            invalidate_capabilities();
            success?.();
        },
        error,
    });
}

export function is_topic_permission_enabled(topic_permissions: {
    read_access: TopicPermissionAccess;
    post_access: TopicPermissionAccess;
    manage_access?: TopicPermissionAccess;
    history_public_to_subscribers?: boolean | null;
}): boolean {
    return Boolean(
        topic_permissions.read_access.direct_members.length +
            topic_permissions.read_access.direct_subgroups.length +
            topic_permissions.post_access.direct_members.length +
            topic_permissions.post_access.direct_subgroups.length ||
            topic_permissions.manage_access?.direct_members.length ||
            topic_permissions.manage_access?.direct_subgroups.length ||
            topic_permissions.history_public_to_subscribers != null,
    );
}
